import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createMap, getMapboxGl, applyMonochrome, applyFlat2D, applyLightPreset } from '../../../../shared/mapbox/mapboxBootstrap';
import { parseToGeoJSON, extractRoute } from './lib/parse';
import { parsePointLapRules } from './lib/classify';
import { buildStage, etaForDistance } from './lib/stage';
import { nearestDistanceKm, passesForPoint } from './lib/route';
import { parseClock, resolveStartMinutes } from './lib/eta';

const TYPE_LABEL = {
    START_NEUTRAL: 'Start (neutral)',
    END_NEUTRAL: 'End of race neutral',
    KOM: 'KOM / QOM',
    SPRINT: 'Sprint',
    FEED: 'Feed zone',
    FINISH: 'Finish',
    START: 'Start',
    VANTAGE: 'Vantage point',
};

const ROUTE_COLOR = '#ff671d'; // TDU brand primary (--primary). White halo (route-halo) carries the
// contrast on the dark 'night' base. Was #F3D925 yellow — changed for brand alignment.

const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

// Gradient → spectator-friendly label. ±1% deadband reads as "flat" (SRTM is too noisy below
// that to claim a slope). Steep climbs are where riders slow down — the best viewing/photos.
const gradeLabel = (pct) => {
    if (pct == null || !Number.isFinite(pct)) return null;
    const r = Math.round(pct);
    if (r >= 1) return `${r}% climb`;
    if (r <= -1) return `${Math.abs(r)}% descent`;
    return 'flat';
};

// "Km to go" target depends on the section, mirroring the official point-to-point sheet: the Start
// (neutral roll-out) row counts down to the End of Race Neutral — i.e. the neutral length, how far
// until racing begins — while every other row counts down to the finish. With no neutral configured
// there's nothing to count to, so the start falls back to the full race distance.
const kmToGo = (type, raceToFinishKm, neutralEndKm = 0) => {
    const isStart = type === 'START_NEUTRAL' || type === 'START';
    return (isStart && neutralEndKm > 0) ? neutralEndKm : raceToFinishKm;
};

// ---- pure render helpers (no React/closure state) -------------------------------------------

/** Build a race-feature marker (KOM / Sprint / Feed / Finish / Vantage…) with its popup. */
function createFeatureMarker(map, mapboxgl, f, neutralEndKm = 0) {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = `tdu-stage-marker tdu-stage-marker--${f.type.toLowerCase()}`;
    const ariaEta = f.multiPass
        ? f.passes.map((p) => `lap ${p.pass} ${p.etaLabel}`).join(', ')
        : f.etaLabel;
    el.setAttribute('aria-label', `${TYPE_LABEL[f.type] || f.type}: ${f.displayLabel || f.label} — estimated ${ariaEta}`);
    // Use the branded icon from the KML when present; fall back to the coloured dot.
    if (f.iconUrl) {
        el.classList.add('tdu-stage-marker--has-icon');
        el.style.backgroundImage = `url("${f.iconUrl}")`;
    }
    // Guard against a non-string KML description (some exports give an object) → never render "[object Object]".
    const detail = (typeof f.description === 'string' && f.description.trim())
        ? `<span class="tdu-stage-popup__detail">${escapeHtml(f.description)}</span>` : '';
    // One consistent meta line for both single- and multi-pass (looping) features:
    // "<lap-1 distance> km · <km to go> km to go · N pass/passes". The per-lap ETAs are listed below
    // for circuits, so the lap-1 distance here is unambiguous. Gradient is intentionally NOT shown —
    // it's a whole-climb average, misleading next to a single point (editor feedback, June 2026).
    const passLabel = f.passes.length === 1 ? '1 pass' : `${f.passes.length} passes`;
    const meta = `${f.raceDistanceKm.toFixed(1)} km · ${kmToGo(f.type, f.raceToFinishKm, neutralEndKm).toFixed(1)} km to go · ${passLabel}`;
    const caravan = f.caravanLabel ? `<span class="tdu-stage-popup__caravan-line">Caravan ~${escapeHtml(f.caravanLabel)}</span>` : '';
    // Multi-pass (circuit) features list every lap's estimate; single-pass keep the caravan line.
    const etaHtml = f.multiPass
        ? `<span class="tdu-stage-popup__eta">Peloton est. ${f.passes.map((p) => `Lap #${p.pass} ${escapeHtml(p.etaLabel)}`).join(' · ')}</span>`
        : `<span class="tdu-stage-popup__eta">Peloton est. ${escapeHtml(f.etaLabel)}</span>${caravan}`;
    // Click a marker → fly to it (the KML's LookAt intent, simplified to centre+zoom).
    el.addEventListener('click', () => map.flyTo({ center: [f.lng, f.lat], zoom: 13, duration: 1200 }));
    return new mapboxgl.Marker({ element: el })
        .setLngLat([f.lng, f.lat])
        // className 'tdu-stage-popup' styles the bubble like the site's product-card popup
        // (brand font, white card, soft shadow) instead of the raw default Mapbox bubble.
        .setPopup(new mapboxgl.Popup({ offset: 14, className: 'tdu-stage-popup' }).setHTML(
            // The label already conveys the kind of point (e.g. "Ziptrak Sprint – Lobethal"), so the
            // type line was redundant — lead with the label instead. Type is still in the aria-label.
            // displayLabel is the friendly, de-truncated, collision-disambiguated name (see stage.js).
            `<strong class="tdu-stage-popup__title">${escapeHtml(f.displayLabel || f.label)}</strong>${detail}`
            + `<span class="tdu-stage-popup__meta">${meta}</span>`
            + etaHtml,
        ))
        .addTo(map);
}

/** Build an ATDW product marker (the RelatedContent "items" style: editor-picked products). */
function createItemMarker(map, mapboxgl, it) {
    const lng = Number(it.lng);
    const lat = Number(it.lat);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
    const el = document.createElement('button');
    el.type = 'button';
    el.className = 'tdu-stage-item-marker';
    // Uniform brand-orange location pin (FA map-marker-alt) for every product — matches the
    // related-content Google map, which renders the same icon brand-coloured (see SearchMap.js).
    // The pin shape + fill live in CSS (mask of /static/images/m_map-marker-alt.svg); no per-product
    // ATDW glyph, so products with no category icon render identically rather than falling back to a dot.
    const img = it.image ? `<img src="${escapeHtml(it.image)}" alt="" class="tdu-stage-item-popup__img">` : '';
    const sub = it.subHeading ? `<br><span class="tdu-stage-item-popup__sub">${escapeHtml(it.subHeading)}</span>` : '';
    const price = it.price ? `<br><span class="tdu-stage-item-popup__price">${escapeHtml(it.price)}</span>` : '';
    const link = it.url ? `<br><a class="tdu-stage-item-popup__link" href="${escapeHtml(it.url)}">View</a>` : '';
    // Pin tip sits on the product's coordinate.
    return new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([lng, lat])
        // Same branded popup chrome as the feature markers (and the related-content product-card).
        .setPopup(new mapboxgl.Popup({ offset: 14, className: 'tdu-stage-popup tdu-stage-popup--item' }).setHTML(
            `${img}<strong class="tdu-stage-popup__title">${escapeHtml(it.title || '')}</strong>${sub}${price}${link}`,
        ))
        .addTo(map);
}

/** Register a small chevron arrowhead once (drawn pointing "up" = along the line's travel direction;
 *  Mapbox rotates it to the line tangent under symbol-placement:line). */
function ensureArrowImage(map) {
    if (map.hasImage('tdu-route-arrow')) return;
    const s = 22;
    const canvas = document.createElement('canvas');
    canvas.width = s;
    canvas.height = s;
    const ctx = canvas.getContext('2d');
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // Drawn pointing RIGHT (east). Under symbol-placement:line Mapbox aligns the icon's +x axis to
    // the line tangent (in coordinate order = direction of travel), so a right-pointing chevron ends
    // up pointing the way the riders go. (An up-pointing one lands 90° off — perpendicular.)
    const chevron = () => {
        ctx.beginPath();
        ctx.moveTo(7, 5);
        ctx.lineTo(16, 11); // apex on the right
        ctx.lineTo(7, 17);
        ctx.stroke();
    };
    // White halo underneath so the blue chevron reads on both the yellow line and the grey base.
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.lineWidth = 6;
    chevron();
    ctx.strokeStyle = '#002f87'; // TDU brand blue — matches the active men's/women's race toggle
    ctx.lineWidth = 3;
    chevron();
    map.addImage('tdu-route-arrow', ctx.getImageData(0, 0, s, s), { pixelRatio: 2 });
}

/** Add the direction-of-travel arrows along the route line (bound to the shared 'route' source, so a
 *  race toggle's setData re-flows them automatically). Idempotent. */
function addRouteArrows(map) {
    ensureArrowImage(map);
    if (map.getLayer('route-arrows')) return;
    map.addLayer({
        id: 'route-arrows',
        type: 'symbol',
        source: 'route',
        layout: {
            'symbol-placement': 'line',
            'symbol-spacing': 85,
            'icon-image': 'tdu-route-arrow',
            'icon-size': 0.85,
            'icon-rotation-alignment': 'map',
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'icon-keep-upright': false,
        },
        // Self-illuminate so the chevrons aren't dimmed by the dark 'night' scene light (see route-line).
        paint: { 'icon-emissive-strength': 1 },
    });
}

/** Swap the route line + all feature markers + camera to `stage` (the toggle teardown path). */
function renderRace(map, mapboxgl, stage, markersRef, neutralEndKm = 0) {
    const src = map.getSource('route');
    if (src) src.setData(stage.routeGeoJSON);
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = stage.features.map((f) => createFeatureMarker(map, mapboxgl, f, neutralEndKm));
    const coords = stage.routeGeoJSON.geometry.coordinates;
    if (coords.length > 1) {
        const bounds = coords.reduce((b, c) => b.extend(c), new mapboxgl.LngLatBounds(coords[0], coords[0]));
        map.fitBounds(bounds, { padding: 48, duration: 0 });
    }
}

/** Hover/tap ETA along the route. Reads the *active* race from `activeRef` so one handler serves
 *  both races after a toggle (no rebind). On a circuit a hovered point belongs to several passes —
 *  list them all rather than snapping to one arbitrary lap. */
function buildHoverHandler(map, activeRef, popup) {
    return (e) => {
        const active = activeRef.current;
        if (!active || !active.stage) return;
        const { stage, eta } = active;
        const routeCoords = stage.routeGeoJSON.geometry.coordinates;
        // Detect passes on the densified line (same one buildStage used for the markers) so a hovered
        // circuit point reports the same lap count as the marker; snap the fallback to the true route.
        const detectLine = stage.detectLine && stage.detectLine.length > 1 ? stage.detectLine : routeCoords;
        const detectCumKm = stage.detectCumKm || stage.cumKm;
        const cursor = [e.lngLat.lng, e.lngLat.lat];
        const passKm = passesForPoint(detectLine, cursor, { threshKm: 0.12, cumKm: detectCumKm });
        const dists = passKm.length ? passKm : [nearestDistanceKm(routeCoords, cursor)];
        let html;
        // Wording mirrors the marker popups (km to go · N pass/passes; no gradient) so a hover and a
        // marker read the same. A hovered circuit point spans several laps at different distances, so
        // the multi-pass line stays a per-lap list rather than a single distance.
        if (dists.length > 1) {
            const laps = dists.map((d, i) => {
                const { etaLabel } = etaForDistance(d, eta);
                return `Lap #${i + 1} ${escapeHtml(etaLabel)}`;
            }).join(' · ');
            html = `<strong>Circuit</strong> · ${dists.length} passes`
                + `<br>Peloton est. ${laps}`;
        } else {
            const d = dists[0];
            const { etaLabel, caravanLabel } = etaForDistance(d, eta);
            // Distances measure from the end of the neutral (race km 0), matching the table/markers,
            // and count down to the official race length when set (else the measured racing length).
            const neutral = eta.neutralEndKm || 0;
            const raceLen = eta.raceLengthKm > 0 ? eta.raceLengthKm : Math.max(0, stage.totalKm - neutral);
            const raceD = Math.max(0, d - neutral);
            const toFinish = Math.max(0, raceLen - raceD);
            const caravan = caravanLabel ? `<br>Caravan ~${escapeHtml(caravanLabel)}` : '';
            html = `<strong>${raceD.toFixed(1)} km</strong> · ${toFinish.toFixed(1)} km to go`
                + `<br>Peloton est. ${escapeHtml(etaLabel)}${caravan}`;
        }
        popup.setLngLat(e.lngLat).setHTML(html).addTo(map);
        map.getCanvas().style.cursor = 'crosshair';
    };
}

/**
 * Normalise inputs to a `races` array. Prefers `inputs.races` (the combined men's/women's page,
 * stages 4–6); falls back to the flat single-race fields so single-race pages and the existing
 * tests are unchanged. Block-level fields (tzLabel, monochrome, fullWidth, items) stay
 * out of here — only the per-race fields belong to a race.
 */
function normaliseRaces(inputs) {
    if (Array.isArray(inputs.races) && inputs.races.length) {
        return inputs.races.map((r, i) => ({ label: r.label || `Race ${i + 1}`, ...r }));
    }
    return [{
        label: inputs.raceLabel || 'Race',
        routeFileUrl: inputs.routeFileUrl,
        raceStartTime: inputs.raceStartTime,
        neutralStartTime: inputs.neutralStartTime,
        pointLaps: inputs.pointLaps,
        averageSpeedKmh: inputs.averageSpeedKmh,
        neutralEndKm: inputs.neutralEndKm,
        raceLengthKm: inputs.raceLengthKm,
        neutralSpeedKmh: inputs.neutralSpeedKmh,
        vantagePoints: inputs.vantagePoints,
        overrides: inputs.overrides,
    }];
}

/**
 * TDU stage map: route line + placemark markers + any-point hover ETA, over a monochrome
 * Mapbox Standard base. Supports a men's/women's race toggle (stages 4–6) and an editor-picked
 * ATDW product layer. All geometry/ETA comes from the unit-tested lib/* utils; this component
 * does fetch + Mapbox wiring + the a11y table.
 */
export default function StageMap({ inputs = {} }) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef([]);       // active race's feature markers (torn down on toggle)
    const itemsMarkersRef = useRef([]);  // product markers (block-level; persist across toggle)
    const activeRef = useRef(null);      // { stage, eta } the persistent hover handler reads
    const [stages, setStages] = useState([]); // parallel to races: { stage } | { error }
    const [activeIndex, setActiveIndex] = useState(0);
    const [mapReady, setMapReady] = useState(false);
    const [showItems, setShowItems] = useState(true);
    // Editor-locked map base: dark 'night' by default (matches the related-content map, makes the
    // orange route pop), 'day' (white) when the author ticks "Use light map". No runtime toggle.
    const lightPreset = inputs.lightMapDefault ? 'day' : 'night';
    const [error, setError] = useState(null);

    const fmt = useMemo(() => ({ ampm: true, tzLabel: inputs.tzLabel || '' }), [inputs.tzLabel]);
    const races = useMemo(() => normaliseRaces(inputs), [inputs]);
    const items = useMemo(() => (Array.isArray(inputs.items) ? inputs.items : []), [inputs.items]);

    const etaParamsFor = (race) => {
        const rollOutMinutes = parseClock(race.neutralStartTime);
        const neutralEndKm = Number(race.neutralEndKm) || 0;
        return {
            // The anchor (official start = end of race neutral) that every ETA measures from. A manual
            // end-of-neutral time wins; otherwise derived from the roll-out + neutral distance/speed so
            // the End-of-neutral row reads a different (later) time than the Start. See lib/eta.js.
            startMinutes: resolveStartMinutes({
                raceStartMinutes: parseClock(race.raceStartTime),
                rollOutMinutes,
                neutralEndKm,
                neutralSpeedKmh: Number(race.neutralSpeedKmh) || 0,
            }),
            // neutralStartTime (roll-out) is shown on the Start row; falls back to the anchor when blank.
            neutralStartMinutes: rollOutMinutes,
            avgSpeedKmh: Number(race.averageSpeedKmh) || 0,
            neutralEndKm,
            // Official racing distance (excl. the neutral) the "km to go" countdown targets; 0/blank
            // falls back to the GPS-measured route length inside buildStage.
            raceLengthKm: Number(race.raceLengthKm) || 0,
            fmt,
        };
    };

    // 1) Fetch + parse every race's route, assemble render-ready stage data (in parallel).
    useEffect(() => {
        let cancelled = false;
        setStages([]);
        Promise.all(races.map(async (race) => {
            if (!race.routeFileUrl) return { error: 'No route file configured.' };
            try {
                const text = await (await fetch(race.routeFileUrl)).text();
                const { line, markers } = extractRoute(parseToGeoJSON(text), {
                    overrides: race.overrides || {},
                    // Per-point lap rules ("Tanunda Sprint = 1") let an editor pin which lap each
                    // repeated circuit point is contested on, by name (see lib/classify). Accept
                    // either NestedContent rows ([{match, laps}]) or the textarea form (one
                    // "name = laps" per line) so the editor surface can change without touching this.
                    pointLaps: Array.isArray(race.pointLaps) ? race.pointLaps : parsePointLapRules(race.pointLaps),
                });
                const vantage = (race.vantagePoints || []).map((v, i) => ({
                    index: 100000 + i, lng: Number(v.lng), lat: Number(v.lat),
                    type: 'VANTAGE', label: v.label || 'Vantage point', isRaceFeature: true,
                }));
                const eta = etaParamsFor(race);
                const stage = buildStage({ line, markers: [...markers, ...vantage], ...eta });
                return { stage, eta };
            } catch (e) {
                return { error: e.message || 'Could not load the route file.' };
            }
        })).then((results) => {
            if (cancelled) return;
            if (results.every((r) => r.error)) { setError(results[0].error); return; }
            setStages(results);
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [races]);

    // 2) Create the map once, when the first active stage is ready. The map persists across
    //    race toggles; only its data/markers are swapped (effect 3).
    useEffect(() => {
        const active = stages[activeIndex];
        if (!active || !active.stage || mapRef.current || !containerRef.current) return undefined;
        let mapboxgl;
        try { mapboxgl = getMapboxGl(); } catch (e) { setError(e.message); return undefined; }

        const coords = active.stage.routeGeoJSON.geometry.coordinates;
        if (coords.length < 2) { setError('Route has no usable geometry.'); return undefined; }
        activeRef.current = active;

        const map = createMap(containerRef.current, {
            style: 'mapbox://styles/mapbox/standard',
            center: coords[Math.floor(coords.length / 2)],
            zoom: 9,
            // Flat, straight top-down view: no tilt, north-up, rotation/pitch locked.
            pitch: 0, bearing: 0, maxPitch: 0,
            dragRotate: false, pitchWithRotate: false, touchPitch: false,
            cooperativeGestures: true,
        });
        mapRef.current = map;
        map.touchZoomRotate.disableRotation();

        // Base config must be set on 'style.load', not 'load' (Standard's config isn't ready yet).
        map.on('style.load', () => {
            // Apply the editor-chosen base (dark 'night' by default; 'day' when "Use light map" is on).
            applyLightPreset(map, lightPreset);
            // monochrome is now an optional greyscale overlay on the dark base (off by default).
            if (inputs.monochrome === true) applyMonochrome(map, true);
            applyFlat2D(map);
        });

        map.on('load', () => {
            map.addSource('route', { type: 'geojson', lineMetrics: true, data: active.stage.routeGeoJSON });
            // Light halo under the route gives a crisp edge on the dark base (a near-black casing is
            // invisible there — same tonal value as the base). Widths are zoom-interpolated so the
            // ribbon keeps a consistent visual weight across zooms instead of a flat 6px that looks
            // thin when zoomed in and heavy when zoomed out.
            const LINE_WIDTH = ['interpolate', ['linear'], ['zoom'], 9, 4, 14, 8];
            const HALO_WIDTH = ['interpolate', ['linear'], ['zoom'], 9, 8, 14, 13];
            // line-emissive-strength:1 makes these self-illuminated. Mapbox Standard lights custom
            // layers with the scene light, so under the dark 'night' preset a plain line is multiplied
            // toward black (the route looked black before this). Emissive = render at true colour.
            map.addLayer({
                id: 'route-halo', type: 'line', source: 'route',
                layout: { 'line-cap': 'round', 'line-join': 'round' },
                paint: { 'line-color': '#ffffff', 'line-width': HALO_WIDTH, 'line-opacity': 0.9, 'line-emissive-strength': 1 },
            });
            map.addLayer({
                id: 'route-line', type: 'line', source: 'route',
                layout: { 'line-cap': 'round', 'line-join': 'round' },
                paint: { 'line-color': ROUTE_COLOR, 'line-width': LINE_WIDTH, 'line-emissive-strength': 1 },
            });

            // Direction-of-travel arrows on top of the route line.
            addRouteArrows(map);

            // Product layer (block-level; added once, persists across toggles, hidden via effect 4).
            itemsMarkersRef.current = items.map((it) => createItemMarker(map, mapboxgl, it)).filter(Boolean);

            // Persistent hover handler — reads the active race from activeRef so it survives toggles.
            const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false });
            const showEta = buildHoverHandler(map, activeRef, popup);
            map.on('mousemove', 'route-line', showEta);
            map.on('click', 'route-line', showEta); // touch
            map.on('mouseleave', 'route-line', () => { popup.remove(); map.getCanvas().style.cursor = ''; });

            renderRace(map, mapboxgl, active.stage, markersRef, active.eta ? active.eta.neutralEndKm || 0 : 0);
            setMapReady(true);
        });

        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stages, activeIndex]);

    // 3) Race toggle: swap data + markers + camera on the existing map (no re-create).
    useEffect(() => {
        if (!mapReady || !mapRef.current) return;
        const active = stages[activeIndex];
        if (!active || !active.stage) return;
        let mapboxgl;
        try { mapboxgl = getMapboxGl(); } catch { return; }
        activeRef.current = active;
        renderRace(mapRef.current, mapboxgl, active.stage, markersRef, active.eta ? active.eta.neutralEndKm || 0 : 0);
    }, [activeIndex, mapReady, stages]);

    // 4) Show/hide the product layer.
    useEffect(() => {
        itemsMarkersRef.current.forEach((m) => {
            const el = m.getElement();
            if (el) el.style.display = showItems ? '' : 'none';
        });
    }, [showItems, mapReady]);

    // 5) Apply the editor-chosen base once the map is ready (covers the case where the map exists
    //    before style.load ran). The route/markers use line-/icon-emissive-strength:1 + their own
    //    colours, so they stay true under either preset; only the basemap lighting changes.
    useEffect(() => {
        if (!mapReady || !mapRef.current) return;
        applyLightPreset(mapRef.current, lightPreset);
    }, [lightPreset, mapReady]);

    if (error) {
        return <div className="tdu-stage-map__error" role="alert">Stage map unavailable: {error}</div>;
    }

    const active = stages[activeIndex];
    const activeStage = active && active.stage ? active.stage : null;
    const activeError = active && active.error ? active.error : null;

    // Table rows: one per (feature, lap), VANTAGE points excluded (map-only), in chronological order.
    // Sorting the flattened rows by distance (not by feature) keeps multi-lap feeds/KOMs interleaved
    // with everything else instead of clumping all of one feature's laps together.
    const tableRows = activeStage
        ? activeStage.features
            .filter((f) => f.type !== 'VANTAGE')
            .flatMap((f) => f.passes.map((p) => ({ f, p })))
            .sort((a, b) => a.p.distanceKm - b.p.distanceKm)
        : [];

    return (
        <div className={`tdu-stage-map${inputs.fullWidth ? ' tdu-stage-map--fullwidth' : ''}`}>
            <div className="tdu-stage-map__toolbar">
                {races.length > 1 && (
                    <div className="tdu-stage-map__race-toggle" role="group" aria-label="Select race">
                        {races.map((r, i) => (
                            <button
                                key={r.label || i}
                                type="button"
                                className={`tdu-stage-map__race-btn${i === activeIndex ? ' is-active' : ''}`}
                                aria-pressed={i === activeIndex}
                                onClick={() => setActiveIndex(i)}
                            >
                                {r.label || `Race ${i + 1}`}
                            </button>
                        ))}
                    </div>
                )}
                {items.length > 0 && (
                    <button
                        type="button"
                        className={`tdu-stage-map__items-btn${showItems ? ' is-active' : ''}`}
                        aria-pressed={showItems}
                        onClick={() => setShowItems((v) => !v)}
                    >
                        {showItems ? 'Hide' : 'Show'} events
                    </button>
                )}
            </div>

            <div ref={containerRef} className="tdu-stage-map__canvas" aria-hidden="true" />

            {activeError && (
                <div className="tdu-stage-map__error" role="alert">This race’s map is unavailable: {activeError}</div>
            )}

            {activeStage && inputs.showTable && (
                <table className="tdu-stage-map__table">
                    <caption>Stage points, gradient and estimated passing times</caption>
                    <thead>
                        <tr>
                            <th scope="col">Point</th>
                            <th scope="col">Type</th>
                            <th scope="col">Distance</th>
                            <th scope="col">Km to go</th>
                            <th scope="col">Grade</th>
                            <th scope="col">Peloton est.</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tableRows.map(({ f, p }) => (
                            // Distance measures from the END of the neutral (race km 0): the neutral
                            // section reads 0.0 km and the race length excludes it (matches markers/popups).
                            // "Km to go" counts to the end of the neutral on the Start row, else to the finish.
                            <tr key={`${f.index}-${p.pass}`}>
                                <td>{f.displayLabel || f.label}{f.multiPass ? ` — Lap #${p.pass}` : ''}</td>
                                <td>{TYPE_LABEL[f.type] || f.type}</td>
                                <td>{p.raceDistanceKm.toFixed(1)} km</td>
                                <td>{kmToGo(f.type, p.raceToFinishKm, active && active.eta ? active.eta.neutralEndKm || 0 : 0).toFixed(1)} km</td>
                                <td>{gradeLabel(p.gradientPct) || '—'}</td>
                                <td>
                                    {p.etaLabel}
                                    {p.caravanLabel && !f.multiPass && (
                                        <span className="tdu-stage-map__caravan"> · caravan ~{p.caravanLabel}</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}
